import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import SiteMeta from '../components/SiteMeta';
import { useNavigation } from '../context/NavigationContext';
import { useNavigationHandler } from '../utils/navigation';
import * as THREE from 'three';
import { createNoise3D } from 'simplex-noise';
import gsap from 'gsap';
import '../Pages.css';
import '../pages/Home.css';
import TextRotation from '../components/TextRotation';
import MixedContentList from '../components/MixedContentList';
import Footer from '../components/Footer';
import Showreel from '../components/Showreel';

function Home() {
  const { destinationPage, activePage, clearDestination, isNavigatingToCaseStudy } = useNavigation();
  const { handleNavClick } = useNavigationHandler();
  const isDestination = destinationPage === 'home';
  const isActive = activePage === 'home';
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const squaresRef = useRef([]);
  const mouseXRef = useRef(0);
  const mouseYRef = useRef(0);
  const timeRef = useRef(0);
  const noise3DRef = useRef(createNoise3D());
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const animationFrameRef = useRef(null);

  const getDiskSize = () => {
    return window.innerWidth <= 768 ? 3 : 7;
  };
  const gridGap = 1;
  const thickness = 0.1;

  const introtextAreaRef = useRef({
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  });

  const calculateGridPadding = () => {
    const squareSize = getDiskSize();
    const viewWidth = (window.innerWidth / 9);

    // Calculate gap dynamically to ensure padding = 0
    // We want: numCols * (squareSize + gap) = viewWidth
    // So: gap = (viewWidth / numCols) - squareSize
    // But we need to determine numCols first, so we'll iterate to find the best fit

    let numCols = Math.ceil(viewWidth / squareSize); // Start with minimum columns
    let gap = ((viewWidth / numCols) - squareSize)*10

    // Ensure gap is not negative and find optimal number of columns
    while (gap < 0 && numCols > 1) {
      numCols--;
      gap = ((viewWidth / numCols) - squareSize)*10
    }

    // If we still have negative gap, set it to a minimum value
    if (gap < 0) {
      gap = 0.1; // Minimum gap
      numCols = Math.floor(viewWidth / (squareSize + gap));
    }
    
    // Store the calculated gap for use in other functions
    window.calculatedGap = gap;
  };

  const updateIntrotextArea = () => {
    const introtext = document.querySelector('.introtext');
    if (introtext) {
      const rect = introtext.getBoundingClientRect();
      introtextAreaRef.current = {
        left: rect.left,
        right: rect.right,
        height: rect.height
      };
    }
  };

  const isInIntrotextArea = (square) => {
    const vector = new THREE.Vector3(square.position.x, square.position.y, square.position.z);
    vector.project(cameraRef.current);
    
    const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
    const y = -(vector.y * 0.5 - 0.5) * window.innerHeight;
    
    const padding = 20;
    
    return x >= (introtextAreaRef.current.left - padding) && 
           x <= (introtextAreaRef.current.right + padding) && 
           y >= (window.innerHeight - introtextAreaRef.current.height - padding) && 
           y <= (window.innerHeight + padding);
  };

  const init = () => {
    if (!containerRef.current) return;

    // Setup scene
    sceneRef.current = new THREE.Scene();
    sceneRef.current.fog = new THREE.Fog(0xffffff, 5, 11);
    sceneRef.current.background = new THREE.Color().setHex(0xffffff);
    
    cameraRef.current = new THREE.OrthographicCamera(
      window.innerWidth / -16,
      window.innerWidth / 16,
      window.innerHeight / 16,
      window.innerHeight / -16,
      1,
      1000
    );
    
    rendererRef.current = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true 
    });
    rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    containerRef.current.appendChild(rendererRef.current.domElement);
    rendererRef.current.shadowMap.enabled = false; // Disable shadows for lighter look

    cameraRef.current.position.z = 6;

    calculateGridPadding();
    updateIntrotextArea();

    // Create grid
    const squareSize = getDiskSize();
    const gap = window.calculatedGap || gridGap; // Use calculated gap if available
    const totalSize = squareSize + gap;
    
    const viewWidth = (window.innerWidth / 9);
    const viewHeight = (window.innerHeight / 9);
    const numCols = Math.ceil(viewWidth / totalSize);
    const numRows = Math.ceil(viewHeight / totalSize) - 2; // Reduce rows by 2
    
    const gridWidth = numCols * totalSize;
    const gridHeight = numRows * totalSize;
    
    const startX = -gridWidth / 2 + totalSize / 2;
    const startY = -gridHeight / 2 + totalSize / 2;

    const geometry = new THREE.SphereGeometry(squareSize/2, 32, 32);
    const material = new THREE.MeshPhongMaterial({ 
      color: 0xff0000,
      side: THREE.FrontSide,
      flatShading: true,
      shininess: 10,
      specular: 0xff2222,
      transparent: true,
      opacity: 0.9
    });

    squaresRef.current = [];

    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        const square = new THREE.Mesh(geometry, material);
        square.position.x = startX + col * totalSize;
        square.position.y = startY + row * totalSize;
        square.originalPosition = {
          x: square.position.x,
          y: square.position.y,
          z: square.position.z
        };
        square.scale.x = 0.5;
        square.scale.y = 0;
        square.scale.z = 0;
        
        if (!isInIntrotextArea(square)) {
          gsap.to(square.scale, {
            x: 1,
            y: 1,
            z: thickness,
            duration: 2,
            delay: 0.2,
            ease: "power1.inOut"
          });
          
          squaresRef.current.push(square);
          sceneRef.current.add(square);
        }
      }
    }

    // Add lights with reduced intensity
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.4);
    sceneRef.current.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(0, 0, 1);
    sceneRef.current.add(directionalLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 0.6);
    backLight.position.set(1, -0.5, -1);
    sceneRef.current.add(backLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.6);
    fillLight.position.set(-1, 0.5, -1);
    sceneRef.current.add(fillLight);
  };

  const onMouseMove = (event) => {
    mouseXRef.current = (event.clientX / window.innerWidth) * 20 - 1;
    mouseYRef.current = -(event.clientY / window.innerHeight) * 20 + 1;
  };

  const onWindowResize = () => {
    if (!cameraRef.current || !rendererRef.current) return;

    if (cameraRef.current.isOrthographicCamera) {
      cameraRef.current.left = window.innerWidth / -16;
      cameraRef.current.right = window.innerWidth / 16;
      cameraRef.current.top = window.innerHeight / 16;
      cameraRef.current.bottom = window.innerHeight / -16;
    }
    cameraRef.current.updateProjectionMatrix();
    rendererRef.current.setSize(window.innerWidth, window.innerHeight);

    calculateGridPadding();
    updateIntrotextArea();
    updateGrid();
  };

  const updateGrid = () => {
    if (!sceneRef.current) return;

    const squareSize = getDiskSize();
    const gap = window.calculatedGap || gridGap; // Use calculated gap if available
    const totalSize = squareSize + gap;
    
    const viewWidth = (window.innerWidth / 9);
    const viewHeight = (window.innerHeight / 9);
    const numCols = Math.ceil(viewWidth / totalSize);
    const numRows = Math.ceil(viewHeight / totalSize) - 2; // Reduce rows by 2
    
    const gridWidth = numCols * totalSize;
    const gridHeight = numRows * totalSize;
    
    const startX = -gridWidth / 2 + totalSize / 2;
    const startY = -gridHeight / 2 + totalSize / 2;

    const material = new THREE.MeshPhongMaterial({ 
      color: 0xff0000,
      side: THREE.FrontSide,
      flatShading: true,
      shininess: 10,
      specular: 0x222222,
      transparent: true,
      opacity: 0.9
    });

    squaresRef.current.forEach(square => {
      sceneRef.current.remove(square);
    });
    squaresRef.current = [];

    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        const geometry = new THREE.SphereGeometry(squareSize/2, 32, 32);
        const square = new THREE.Mesh(geometry, material);
        square.position.x = startX + col * totalSize;
        square.position.y = startY + row * totalSize;
        square.originalPosition = {
          x: square.position.x,
          y: square.position.y,
          z: square.position.z
        };
        square.scale.set(0, 0, 0);
        
        if (!isInIntrotextArea(square)) {
          gsap.to(square.scale, {
            x: 1,
            y: 1,
            z: thickness,
            duration: 0.5,
            ease: "back.out(1.7)"
          });

          squaresRef.current.push(square);
          sceneRef.current.add(square);
        }
      }
    }

    calculateGridPadding();
  };

  const animate = () => {
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;

    timeRef.current += 0.0025;

    squaresRef.current.forEach(square => {
      const deltaX = mouseXRef.current * 15 - square.originalPosition.x;
      const deltaY = mouseYRef.current * 15 - square.originalPosition.y;

      const maxRotation = 0.75;
      const targetY = (deltaX / 30) * maxRotation;
      const targetX = (deltaY / 30) * maxRotation;

      const noiseScale = 0.005;
      const noiseAmplitude = 1.2;
      
      const noiseRotX = noise3DRef.current(
        square.originalPosition.x * noiseScale, 
        square.originalPosition.y * noiseScale, 
        timeRef.current
      ) * noiseAmplitude;
      
      const noiseRotY = noise3DRef.current(
        square.originalPosition.x * noiseScale, 
        square.originalPosition.y * noiseScale, 
        timeRef.current + 100
      ) * noiseAmplitude;

      square.rotation.y += (targetY + noiseRotY - square.rotation.y) * 0.05;
      square.rotation.x += (targetX + noiseRotX - square.rotation.x) * 0.05;
    });

    rendererRef.current.render(sceneRef.current, cameraRef.current);
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  const handleCasesClick = (e) => {
    e.preventDefault();
    handleNavClick('work');
  };

  useEffect(() => {
    const container = containerRef.current;

    init();
    animate();

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('resize', onWindowResize);
    window.addEventListener('scroll', updateIntrotextArea);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', onWindowResize);
      window.removeEventListener('scroll', updateIntrotextArea);

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      if (rendererRef.current && container) {
        container.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();
      }

      squaresRef.current.forEach(square => {
        if (sceneRef.current) {
          sceneRef.current.remove(square);
          square.geometry.dispose();
          square.material.dispose();
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SiteMeta
        title="Robin Gardeur (le Shka) - Director x Digital Artist"
        description="Director x Digital Artist with 15 years in the industry, 14 awards, and 100+ clients. Design Director, Art Director, and Co-Founder with experience at top creative studios."
      />
      <motion.div
        className={`page home-page ${isDestination ? 'page-transitioning' : ''} ${isActive ? 'page-active' : ''} ${isNavigatingToCaseStudy ? 'page-fade-to-white' : ''}`}
        initial={{ y: '100vh' }}
        animate={{ y: 0 }}
        exit={{ y: '-20vh' }}
        transition={{
          duration: 1,
          ease: [0.4, 0, 0.2, 1],
          type: "tween"
        }}
        onAnimationComplete={() => {
          if (isDestination) {
            clearDestination();
          }
        }}
      >


        <div className="homeintro">
        <div ref={containerRef} id="canvas-container" style={{ top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }} />
        <div className="introtext">
          <h2>Director x Digital Artist.</h2>
          <div className="introtext-content">
            <ul>
              <li>Co-Founder</li>
              <li>Design Director</li>
              <li>Art Director</li>
              <li>&nbsp;</li>
              <li>&nbsp;</li>
              <li>&nbsp;</li>
              <li>Designer</li>
      
            </ul>
            <ul>
              <li>Studio Inconnu</li>
              <li>Landor</li>
              <li>Allofus</li>
              <li>Random Studio</li>
              <li>Hi-Res!</li>
              <li>Leonard (Formerly Werkstatt)</li>
              <li>HKI (Hellohikimori)</li>
 
            </ul>
            <ul>
              <li>15 Years working in the industry</li>
              <li>14 Awards</li>
              <li>100+ Clients</li>
            </ul>
          </div>

          {/* </h2>
          <RolesAnimation />
          <h2 style={{ display: 'block' }}>
            I lead and design unique identities and immersive experiences.</h2> */}
        </div>
      </div>

      <Showreel />
      <TextRotation />
      <MixedContentList />

      <div className="cases-button-container">
        <a href="/cases" className="cases-button" onClick={handleCasesClick}>
          <div className="cases-text-mask">
            <div className="cases-text-line">
              <div className="cases-text-top">See all my work</div>
              <div className="cases-text-bottom">See all my work</div>
            </div>
          </div>
        </a>
      </div>
      <Footer />
      </motion.div>
    </>
  );
}

export default Home;